import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { formatDateTime } from "@/lib/format";
import { CreditCard, ShieldCheck, Save, Loader2, Zap, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Super Admin platform-level Payment Gateway config.
 * Manages Akomoda's OWN Stripe / PayPal used to charge hotels for subscriptions,
 * distinct from per-hotel BYOK Stripe used to charge guests.
 */
export default function SuperAdminPaymentGateways() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/payment-gateways");
            setCfg(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <>
            <Topbar
                title="Payment Gateways"
                subtitle="Kredencialet e platformës për të faturuar hotelet"
            />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mx-auto grid max-w-4xl gap-4">
                    {loading || !cfg ? (
                        <div className="py-16 text-center text-sm text-muted-foreground">Duke ngarkuar…</div>
                    ) : (
                        <>
                            <StripeCard cfg={cfg.stripe} onSaved={load} />
                            <PayPalCard cfg={cfg.paypal} onSaved={load} />
                        </>
                    )}

                    <div className="mt-2 rounded-lg border border-border bg-muted/40 p-4 text-[12px] text-muted-foreground">
                        <div className="mb-1 flex items-center gap-1 font-semibold text-foreground">
                            <ShieldCheck size={12} /> Shënim për sigurinë
                        </div>
                        Kredencialet ruhen në databazë. Për prodhim, konfiguroni enkriptim në rest ose përdorni një menaxher secret (AWS KMS, HashiCorp Vault).
                    </div>
                </div>
            </div>
        </>
    );
}

// ── Stripe ─────────────────────────────────────────────────────
function StripeCard({ cfg, onSaved }) {
    const [form, setForm] = useState({
        publishable_key: cfg.publishable_key || "",
        secret_key: "",
        webhook_secret: "",
        mode: cfg.mode || "test",
    });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/payment-gateways/stripe", form);
            toast.success("Cilësimet e Stripe u ruajtën");
            setForm({ ...form, secret_key: "", webhook_secret: "" });
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true); setTestResult(null);
        try {
            const { data } = await api.post("/admin/payment-gateways/stripe/test");
            setTestResult({ ok: true, msg: `Lidhur me llogarinë ${data.account_id}${data.business_name ? ` · ${data.business_name}` : ""} · ${data.latency_ms}ms${data.livemode ? " · LIVE" : " · TEST"}` });
            toast.success("Stripe: e lidhur");
            onSaved();
        } catch (e) {
            setTestResult({ ok: false, msg: formatApiErrorDetail(e.response?.data?.detail) || "Test dështoi" });
            toast.error("Stripe: dështoi");
        } finally { setTesting(false); }
    };

    return (
        <form onSubmit={save} className="rounded-xl border border-border bg-card p-5" data-testid="stripe-config-card">
            <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#635bff]/10 text-[#635bff]">
                        <CreditCard size={18} />
                    </div>
                    <div>
                        <div className="text-sm font-semibold tracking-tight text-foreground">Stripe</div>
                        <div className="text-[11px] text-muted-foreground">Karta, checkout dhe abonime</div>
                    </div>
                </div>
                <StatusBadge status={cfg.connection_status} testedAt={cfg.connection_tested_at} />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
                <Field label="Publishable key">
                    <input value={form.publishable_key} onChange={(e) => setForm({ ...form, publishable_key: e.target.value })}
                           placeholder="pk_test_… ose pk_live_…" className="akm-input" data-testid="stripe-publishable-key" />
                </Field>
                <Field label="Mode">
                    <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="akm-input">
                        <option value="test">Test</option>
                        <option value="live">Live</option>
                    </select>
                </Field>
                <Field label="Secret key" hint={cfg.secret_key_set ? `Aktualisht: ${cfg.secret_key_masked}` : "Nuk është vendosur"} className="md:col-span-2">
                    <input type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                           placeholder={cfg.secret_key_set ? "Lëre bosh për të mos ndryshuar" : "sk_test_… ose sk_live_…"}
                           className="akm-input" data-testid="stripe-secret-key" />
                </Field>
                <Field label="Webhook secret" hint={cfg.webhook_secret_set ? `Aktualisht: ${cfg.webhook_secret_masked}` : "Opsionale"} className="md:col-span-2">
                    <input type="password" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                           placeholder={cfg.webhook_secret_set ? "Lëre bosh për të mos ndryshuar" : "whsec_…"}
                           className="akm-input" data-testid="stripe-webhook-secret" />
                </Field>
            </div>

            {testResult && (
                <div className={`mt-3 flex items-start gap-2 rounded-md border p-2.5 text-[11.5px] ${testResult.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"}`}
                     data-testid="stripe-test-result">
                    {testResult.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                    <div className="flex-1">{testResult.msg}</div>
                </div>
            )}

            <div className="mt-4 flex items-center gap-2">
                <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="stripe-save">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Ruaj
                </button>
                <button type="button" onClick={test} disabled={testing || !cfg.secret_key_set} className="akm-btn-ghost" data-testid="stripe-test">
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    Test lidhjen
                </button>
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer"
                   className="ml-auto text-[11.5px] text-muted-foreground hover:text-foreground">
                    Merr çelësat në Stripe <ExternalLink size={10} className="inline" />
                </a>
            </div>
        </form>
    );
}

// ── PayPal ─────────────────────────────────────────────────────
function PayPalCard({ cfg, onSaved }) {
    const [form, setForm] = useState({
        client_id: cfg.client_id || "",
        client_secret: "",
        mode: cfg.mode || "sandbox",
    });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/payment-gateways/paypal", form);
            toast.success("Cilësimet e PayPal u ruajtën");
            setForm({ ...form, client_secret: "" });
            onSaved();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true); setTestResult(null);
        try {
            const { data } = await api.post("/admin/payment-gateways/paypal/test");
            setTestResult({ ok: true, msg: `App: ${data.app_id || "—"} · scope: ${data.scope || "…"} · ${data.latency_ms}ms · ${data.mode.toUpperCase()}` });
            toast.success("PayPal: e lidhur");
            onSaved();
        } catch (e) {
            setTestResult({ ok: false, msg: formatApiErrorDetail(e.response?.data?.detail) || "Test dështoi" });
            toast.error("PayPal: dështoi");
        } finally { setTesting(false); }
    };

    return (
        <form onSubmit={save} className="rounded-xl border border-border bg-card p-5" data-testid="paypal-config-card">
            <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#003087]/10 text-[#003087] dark:text-[#66a3ff]">
                        <CreditCard size={18} />
                    </div>
                    <div>
                        <div className="text-sm font-semibold tracking-tight text-foreground">PayPal</div>
                        <div className="text-[11px] text-muted-foreground">PayPal dhe checkout me kartë</div>
                    </div>
                </div>
                <StatusBadge status={cfg.connection_status} testedAt={cfg.connection_tested_at} />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
                <Field label="Client ID" className="md:col-span-2">
                    <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                           placeholder="AY…" className="akm-input" data-testid="paypal-client-id" />
                </Field>
                <Field label="Client secret" hint={cfg.client_secret_set ? `Aktualisht: ${cfg.client_secret_masked}` : "Nuk është vendosur"} className="md:col-span-2">
                    <input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                           placeholder={cfg.client_secret_set ? "Lëre bosh për të mos ndryshuar" : "EL…"}
                           className="akm-input" data-testid="paypal-client-secret" />
                </Field>
                <Field label="Mode">
                    <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="akm-input">
                        <option value="sandbox">Sandbox</option>
                        <option value="live">Live</option>
                    </select>
                </Field>
            </div>

            {testResult && (
                <div className={`mt-3 flex items-start gap-2 rounded-md border p-2.5 text-[11.5px] ${testResult.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"}`}
                     data-testid="paypal-test-result">
                    {testResult.ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                    <div className="flex-1">{testResult.msg}</div>
                </div>
            )}

            <div className="mt-4 flex items-center gap-2">
                <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="paypal-save">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Ruaj
                </button>
                <button type="button" onClick={test} disabled={testing || !cfg.client_secret_set} className="akm-btn-ghost" data-testid="paypal-test">
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    Test lidhjen
                </button>
                <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer"
                   className="ml-auto text-[11.5px] text-muted-foreground hover:text-foreground">
                    Merr aplikacion në PayPal <ExternalLink size={10} className="inline" />
                </a>
            </div>
        </form>
    );
}

function Field({ label, hint, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-[10.5px] text-muted-foreground">{hint}</span>}
        </label>
    );
}

function StatusBadge({ status, testedAt }) {
    if (status === "connected") {
        return (
            <div className="text-right">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    <CheckCircle2 size={11} /> Lidhur
                </span>
                {testedAt && <div className="mt-0.5 text-[9.5px] text-muted-foreground">{formatDateTime(testedAt)}</div>}
            </div>
        );
    }
    if (status === "error") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10.5px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                <AlertCircle size={11} /> Dështoi
            </span>
        );
    }
    return (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
            E patestuar
        </span>
    );
}
